/**
 * Service worker registration, and the part vite-plugin-pwa's generated
 * `registerSW.js` leaves out: actually putting a new version on screen.
 *
 * `autoUpdate` keeps the *precache* fresh — a new worker installs, claims the
 * clients, and deletes the old cache. What it does not do is refresh a page
 * that has already rendered. An installed PWA launches from the precache
 * instantly, the new worker takes over a moment later, and the user goes on
 * looking at the previous build until they happen to launch it again.
 *
 * That is not theoretical: a retry button shipped, worked in a browser tab,
 * and was invisible in the installed app on the same account at the same time.
 *
 * So a takeover triggers a reload — but never while the user is looking at the
 * screen. Reloading mid-task would throw away a half-filled receipt form,
 * which is a worse failure than being one version behind for a few minutes.
 * The reload happens while the app is hidden, so the next launch is simply
 * correct.
 */

/** How often a running app asks whether a new version exists. */
const UPDATE_CHECK_MS = 30 * 60 * 1000

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return

  // Never in dev. `devOptions.enabled` is false, so no `/sw.js` is generated
  // and the request falls through to Vite's SPA handler, which returns
  // index.html — the browser then logs an unsupported-MIME-type error on every
  // page load. Nothing breaks, but a permanent red error in the console is
  // exactly the kind of noise that trains you to ignore real ones.
  if (!import.meta.env.PROD) return

  // A page with no controller is a first visit: `clientsClaim` fires
  // `controllerchange` there too, and reloading then would be a pointless
  // refresh of a page that is already current.
  const hadController = navigator.serviceWorker.controller !== null
  let pendingReload = false

  /**
   * Whether reloading now would throw away something the user typed.
   *
   * Deliberately crude — it only has to distinguish "mid-way through filing a
   * receipt" from "reading a screen". Waiting for the tab to be hidden was the
   * previous rule and it was wrong: a desktop tab that stays open and gets
   * refreshed may never go hidden, so a deployed fix stayed invisible and the
   * app looked broken in a way no amount of reloading solved.
   */
  const hasUnsavedInput = (): boolean => {
    const active = document.activeElement
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
      return true
    }
    return Array.from(document.querySelectorAll('input, textarea')).some((field) => {
      if (field instanceof HTMLInputElement) {
        if (field.type === 'checkbox' || field.type === 'radio') {
          return field.checked !== field.defaultChecked
        }
        return field.value !== field.defaultValue
      }
      return field instanceof HTMLTextAreaElement && field.value !== field.defaultValue
    })
  }

  const reloadWhenSafe = () => {
    if (document.visibilityState === 'hidden' || !hasUnsavedInput()) {
      window.location.reload()
      return
    }
    // Something is half-typed. Wait for the tab to be put aside rather than
    // discarding it mid-sentence.
    pendingReload = true
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && pendingReload) {
      window.location.reload()
    }
  })

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) return
    reloadWhenSafe()
  })

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        // A phone keeps this app open for days. Without a periodic check the
        // only update opportunity is a cold launch, which may never come.
        setInterval(() => void registration.update(), UPDATE_CHECK_MS)

        // Returning to the app is the other natural moment to check, and the
        // one that matters after a deploy.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            void registration.update()
          }
        })
      })
      .catch(() => {
        // A failed registration costs offline capability, nothing else. The
        // app works without it, so this must not surface as an error.
      })
  })
}
